import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { chargeCode, formatChargeCode } from '@pulsepoint/shared';
import { useMemo, useState } from 'react';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { socketLetter, useQrStickers } from './api';

/** A socket's choice: a sticker's code, or null for a new code made now (to print later). */
export interface SocketSticker {
  connectorId: number;
  code: string | null;
}

/**
 * Which QR sticker goes on each socket: one of the stickers made ahead and not on a charger
 * yet (pick it, or type the code printed on it), or a new code, saved for printing later.
 */
export function ChooseStickersDialog({ title, lead, connectorIds, connectorCount, confirmLabel, close }: { title: string; lead: string; connectorIds: number[]; connectorCount: number; confirmLabel: string } & DialogProps<SocketSticker[]>) {
  const stickers = useQrStickers();
  const free = useMemo(() => (stickers.data ?? []).filter((s) => !s.charger).map((s) => formatChargeCode(s.code)), [stickers.data]);
  const [chosen, setChosen] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});

  const setFor = (n: number, v: string) => {
    setChosen((c) => ({ ...c, [n]: v }));
    setErrors((e) => ({ ...e, [n]: '' }));
  };

  const done = () => {
    const out: SocketSticker[] = [];
    const bad: Record<number, string> = {};
    const seen = new Set<string>();
    for (const n of connectorIds) {
      const typed = (chosen[n] ?? '').trim();
      if (!typed) {
        out.push({ connectorId: n, code: null });
        continue;
      }
      const parsed = chargeCode.safeParse(typed);
      if (!parsed.success) bad[n] = parsed.error.issues[0]?.message ?? 'Check the code';
      else if (seen.has(parsed.data)) bad[n] = 'Already chosen for another socket';
      else {
        seen.add(parsed.data);
        out.push({ connectorId: n, code: parsed.data });
      }
    }
    if (Object.keys(bad).length) return setErrors(bad);
    close(out);
  };

  return (
    <Dialog open onClose={() => close()} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 2.5 }}>
          {lead} Choose a sticker from Settings, QR stickers, or type the code printed on it. Left blank, a new code is made, ready to print later.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {connectorIds.map((n) => (
            <Autocomplete
              key={n}
              freeSolo
              autoSelect
              options={free.filter((code) => code === chosen[n] || !Object.values(chosen).includes(code))}
              inputValue={chosen[n] ?? ''}
              onInputChange={(_e, v) => setFor(n, v)}
              loading={stickers.isPending}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={connectorCount > 1 ? `${socketLetter(n)} sticker` : 'Sticker'}
                  placeholder={free.length ? `${free.length} not on a charger · blank for a new code` : 'Blank for a new code'}
                  error={!!errors[n]}
                  helperText={errors[n] || ' '}
                  sx={{ '& input': { letterSpacing: '0.08em' } }}
                />
              )}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>Cancel</Button>
        <Button variant="contained" onClick={done}>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}
